'use client'

import classNames from 'classnames'
import { useParams } from 'next/navigation'
import { formatReferencePrice, getReferenceCurrency } from 'tokyu-hooks'

import { useTranslation } from 'app/i18n/client'

interface ReferencePriceProps {
  currency?: string
  amountCents?: number
  // Omit `part` to render the caption and amount together on one line
  // (used on the detail page). Pass 'label' to render only the "Reference
  // Price" caption (placed under the price label) and 'value' to render only
  // the formatted foreign amount (placed under the price amount) — the split
  // layout used on the search-results list. All variants share the same
  // visibility guard, so they appear/hide together.
  part?: 'label' | 'value'
  className?: string
  // Undated mode: the shown amount is the cheapest of the month, not a fixed
  // total, so append a "~" right after the formatted value (mirrors the "~"
  // already appended after JPY prices elsewhere in undated mode).
  approx?: boolean
}

const ReferencePrice: React.FC<ReferencePriceProps> = ({
  currency,
  amountCents,
  part,
  className,
  approx,
}) => {
  const { t } = useTranslation()
  const { locale } = useParams<{ locale: string }>() || {}

  // Reference price is locale-driven: Japanese (and any locale without a
  // mapped reference currency) shows only the JPY value. The availability API
  // defaults mcp_* to a local currency (e.g. IDR) when none is requested, so
  // guard on the locale rather than trusting the currency the API returned.
  if (!getReferenceCurrency(locale)) return null

  const formatted = formatReferencePrice(currency, amountCents)

  if (!formatted) return null

  return (
    <div
      className={classNames(
        // The 'label' caption is wrappable by default: on the narrow
        // search-result card it shares a grid column with the price label, and
        // pinning it to one line there sets the column's minimum width and
        // pushes the price row past the card edge. Callers with room for one
        // line (the desktop card, which gives the caption its own full-width
        // grid cell) pass `whitespace-nowrap` in className. Amounts are always
        // unbreakable.
        part !== 'label' && 'whitespace-nowrap',
        !part && 'flex flex-wrap gap-x-1',
        className,
      )}
    >
      {part === 'label' ? (
        t('Reference Price')
      ) : part === 'value' ? (
        <>
          {formatted}
          {approx && '~'}
        </>
      ) : (
        <>
          <span>{t('Reference Price')}</span>
          <span>
            {formatted}
            {approx && '~'}
          </span>
        </>
      )}
    </div>
  )
}

export default ReferencePrice
